/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  CreateHelpArticlePathParameters,
  CreateHelpArticleRequest,
  CreateHelpCategoryPathParameters,
  CreateHelpCategoryRequest,
  DeleteHelpArticlePathParameters,
  DeleteHelpCategoryPathParameters,
  GetStoreHelpArticlePathParameters,
  HelpArticle,
  HelpCategory,
  ListHelpArticleFeedback200,
  ListHelpArticleFeedbackParams,
  ListHelpArticleFeedbackPathParameters,
  ListStoreHelpArticles200,
  ListStoreHelpArticlesParams,
  ListStoreHelpArticlesPathParameters,
  ListStoreHelpCategories200,
  ListStoreHelpCategoriesPathParameters,
  UpdateHelpArticlePathParameters,
  UpdateHelpArticleRequest,
  UpdateHelpCategoryPathParameters,
  UpdateHelpCategoryRequest
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Every category, internal ones included, with how many articles each holds and how many of those shoppers can read. At most 50 per store
 * @summary Help categories of the store, by position
 */
export const listStoreHelpCategories = (
    { storeId }: ListStoreHelpCategoriesPathParameters,
 options?: SecondParameter<typeof customInstance<ListStoreHelpCategories200>>,) => {
      return customInstance<ListStoreHelpCategories200>(
      {url: `/admin/v1/stores/${storeId}/help/categories`, method: 'GET'
    },
      options);
    }
  /**
 * Internal categories, and every article in them, stay off the storefront. The handles "a" and "search" are reserved
 * @summary Create a help category
 */
export const createHelpCategory = (
    { storeId }: CreateHelpCategoryPathParameters,
    createHelpCategoryRequest: BodyType<CreateHelpCategoryRequest>,
 options?: SecondParameter<typeof customInstance<HelpCategory>>,) => {
      return customInstance<HelpCategory>(
      {url: `/admin/v1/stores/${storeId}/help/categories`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createHelpCategoryRequest
    },
      options);
    }
  /**
 * @summary Update a help category; renaming the handle writes a 301
 */
export const updateHelpCategory = (
    { storeId, id }: UpdateHelpCategoryPathParameters,
    updateHelpCategoryRequest: BodyType<UpdateHelpCategoryRequest>,
 options?: SecondParameter<typeof customInstance<HelpCategory>>,) => {
      return customInstance<HelpCategory>(
      {url: `/admin/v1/stores/${storeId}/help/categories/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateHelpCategoryRequest
    },
      options);
    }
  /**
 * @summary Delete a help category; its articles stay and lose the category
 */
export const deleteHelpCategory = (
    { storeId, id }: DeleteHelpCategoryPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/help/categories/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * `q` matches title, handle, excerpt and keywords; the inbox composer searches published articles this way and inserts `url` (null for anything shoppers cannot read)
 * @summary Help articles of the store, newest first unless sorted
 */
export const listStoreHelpArticles = (
    { storeId }: ListStoreHelpArticlesPathParameters,
    params?: ListStoreHelpArticlesParams,
 options?: SecondParameter<typeof customInstance<ListStoreHelpArticles200>>,) => {
      return customInstance<ListStoreHelpArticles200>(
      {url: `/admin/v1/stores/${storeId}/help/articles`, method: 'GET',
        params
    },
      options);
    }
  /**
 * `bodyHtml` is sanitised on write. Publishing dates the article the first time; `live` says whether shoppers can read it (published, public, not in an internal category). At most 2,000 per store
 * @summary Create a help article
 */
export const createHelpArticle = (
    { storeId }: CreateHelpArticlePathParameters,
    createHelpArticleRequest: BodyType<CreateHelpArticleRequest>,
 options?: SecondParameter<typeof customInstance<HelpArticle>>,) => {
      return customInstance<HelpArticle>(
      {url: `/admin/v1/stores/${storeId}/help/articles`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createHelpArticleRequest
    },
      options);
    }
  /**
 * @summary One help article
 */
export const getStoreHelpArticle = (
    { storeId, id }: GetStoreHelpArticlePathParameters,
 options?: SecondParameter<typeof customInstance<HelpArticle>>,) => {
      return customInstance<HelpArticle>(
      {url: `/admin/v1/stores/${storeId}/help/articles/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Update a help article; renaming the handle writes a 301
 */
export const updateHelpArticle = (
    { storeId, id }: UpdateHelpArticlePathParameters,
    updateHelpArticleRequest: BodyType<UpdateHelpArticleRequest>,
 options?: SecondParameter<typeof customInstance<HelpArticle>>,) => {
      return customInstance<HelpArticle>(
      {url: `/admin/v1/stores/${storeId}/help/articles/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateHelpArticleRequest
    },
      options);
    }
  /**
 * @summary Delete a help article and its votes
 */
export const deleteHelpArticle = (
    { storeId, id }: DeleteHelpArticlePathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/help/articles/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * @summary Votes and comments on a help article, newest first
 */
export const listHelpArticleFeedback = (
    { storeId, id }: ListHelpArticleFeedbackPathParameters,
    params?: ListHelpArticleFeedbackParams,
 options?: SecondParameter<typeof customInstance<ListHelpArticleFeedback200>>,) => {
      return customInstance<ListHelpArticleFeedback200>(
      {url: `/admin/v1/stores/${storeId}/help/articles/${id}/feedback`, method: 'GET',
        params
    },
      options);
    }
  export type ListStoreHelpCategoriesResult = NonNullable<Awaited<ReturnType<typeof listStoreHelpCategories>>>
export type CreateHelpCategoryResult = NonNullable<Awaited<ReturnType<typeof createHelpCategory>>>
export type UpdateHelpCategoryResult = NonNullable<Awaited<ReturnType<typeof updateHelpCategory>>>
export type DeleteHelpCategoryResult = NonNullable<Awaited<ReturnType<typeof deleteHelpCategory>>>
export type ListStoreHelpArticlesResult = NonNullable<Awaited<ReturnType<typeof listStoreHelpArticles>>>
export type CreateHelpArticleResult = NonNullable<Awaited<ReturnType<typeof createHelpArticle>>>
export type GetStoreHelpArticleResult = NonNullable<Awaited<ReturnType<typeof getStoreHelpArticle>>>
export type UpdateHelpArticleResult = NonNullable<Awaited<ReturnType<typeof updateHelpArticle>>>
export type DeleteHelpArticleResult = NonNullable<Awaited<ReturnType<typeof deleteHelpArticle>>>
export type ListHelpArticleFeedbackResult = NonNullable<Awaited<ReturnType<typeof listHelpArticleFeedback>>>
